import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminPressKitService } from '../services/admin.pressKit.service'
import type { PressKitReleaseWithId, PressKitReleaseFormValues } from '@/features/press-kit/types'

interface UseAdminPressKitReleasesResult {
  releases: PressKitReleaseWithId[]
  loading: boolean
  error: string | null
  createRelease: (values: PressKitReleaseFormValues) => Promise<void>
  updateRelease: (id: string, values: Partial<PressKitReleaseFormValues>) => Promise<void>
  deleteRelease: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminPressKitReleases(): UseAdminPressKitReleasesResult {
  const [releases, setReleases] = useState<PressKitReleaseWithId[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminPressKitService
      .getAllReleases()
      .then(setReleases)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load releases'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createRelease = useCallback(async (values: PressKitReleaseFormValues): Promise<void> => {
    await adminPressKitService.createRelease(values)
    refresh()
  }, [refresh])

  const updateRelease = useCallback(
    async (id: string, values: Partial<PressKitReleaseFormValues>): Promise<void> => {
      await adminPressKitService.updateRelease(id, values)
      refresh()
    },
    [refresh]
  )

  const deleteRelease = useCallback(async (id: string): Promise<void> => {
    await adminPressKitService.deleteRelease(id)
    refresh()
  }, [refresh])

  return { releases, loading, error, createRelease, updateRelease, deleteRelease, refresh }
}
